import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
  useNodes,
  useEdges,
} from "@xyflow/react";
import {
  GitBranch,
  Trash2,
  Settings,
  X,
  FileText,
  ChevronsRight,
  Loader2,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { getNodeValidationStatus } from "../../utils/nodeValidation";
import { formatDragVariable } from "../../utils/variableHelper";
import { getAllAncestorNodes } from "../../utils/workflowHelpers";
import { NodeDataSidebar } from "../../common/shared/NodeDataSidebar";
import NodeDocsButton from "../../docs/NodeDocsButton";

interface FileInput {
  _id: string;
  name: string;
  url: string;
}

export const CompareDataNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [sourceFileId, setSourceFileId] = useState<string>(
    data?.source_file_id || ""
  );
  const [targetFileId, setTargetFileId] = useState<string>(
    data?.target_file_id || ""
  );
  const [documentStoreId, setDocumentStoreId] = useState<string>(
    data?.document_store_id || ""
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setIsDraggingOver] = useState(false);
  const { setNodes, deleteElements, getNodes, getEdges } = useReactFlow();
  const nodes = useNodes();
  const edges = useEdges();
  const [errors, setErrors] = useState<string[]>([]);

  const executionStatus = data?.executionStatus || null;
  const isExecuting = executionStatus === "running";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  // Build list of previous nodes for the sidebar (Ancestors)
  const previousNodes = useMemo(() => {
    const ancestors = getAllAncestorNodes(id, nodes, edges);
    return ancestors.map((n) => ({
      nodeId: n.id,
      nodeName: n.data?.label || n.data?.name || n.id,
      nodeType: n.type || "unknown",
      inputs: n.data?.inputs || {},
      outputs: n.data?.outputs || {},
    }));
  }, [edges, id, nodes]);

  const allNodes = getNodes();
  const incomingEdges = getEdges().filter((edge) => edge.target === id);

  const previousNodesOutputs = useMemo(() => {
    const outputs: { nodeId: string; nodeName: string; files: FileInput[] }[] =
      [];
    incomingEdges.forEach((edge) => {
      const sourceNode: any = allNodes.find((n) => n.id === edge.source);
      if (sourceNode && sourceNode.data?.outputs?.file_ids) {
        outputs.push({
          nodeId: sourceNode.id,
          nodeName: sourceNode.data.name || sourceNode.type || sourceNode.id,
          files: sourceNode.data.outputs.file_ids,
        });
      }
    });
    return outputs;
  }, [allNodes, incomingEdges]);

  const allFiles = useMemo(() => {
    const fileMap = new Map<string, FileInput>();
    previousNodesOutputs.forEach((output) => {
      output.files.forEach((file) => {
        if (file._id && file.name && file.name.toLowerCase().endsWith(".pdf")) {
          fileMap.set(file._id, file);
        }
      });
    });
    return Array.from(fileMap.values());
  }, [previousNodesOutputs]);

  const sourceFileName =
    allFiles.find((f) => f._id === sourceFileId)?.name || "Not Selected";
  const targetFileName =
    allFiles.find((f) => f._id === targetFileId)?.name || "Not Selected";

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => e.stopPropagation(),
    []
  );

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };
  const handleCloseModal = () => setIsModalOpen(false);

  const validateNode = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    const currentNode = nodes.find((n) => n.id === id);
    if (currentNode) {
      const { errors } = getNodeValidationStatus(currentNode, edges, nodes);
      setErrors(errors);
    }
  }, [getNodes, getEdges, id]);

  // Drag & Drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as unknown as globalThis.Node))
      return;
    setIsDraggingOver(false);
  };

  const handleDrop = (e: React.DragEvent, targetField: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    try {
      const fullVariable = e.dataTransfer.getData("text/plain");
      if (!fullVariable) return;
      if (targetField === "document_store_id") {
        setDocumentStoreId(fullVariable);
      }
    } catch (err) {
      console.error("Failed to parse dropped data", err);
    }
  };

  const handleModalDragStart = (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output"
  ) => {
    e.stopPropagation();
    const fullVariable = formatDragVariable(nodeName, variable, type);
    e.dataTransfer.setData("text/plain", fullVariable);
    e.dataTransfer.effectAllowed = "copy";
  };

  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                source_file_id: sourceFileId,
                target_file_id: targetFileId,
                document_store_id: documentStoreId,
              },
            }
          : node
      )
    );
  }, [sourceFileId, targetFileId, documentStoreId, id, setNodes]);

  useEffect(() => {
    validateNode();
  }, [data, validateNode]);

  const statusConfig = useMemo(() => {
    if (errors.length > 0) {
      return { bg: "bg-red-500", text: "Error" };
    }
    if (sourceFileId && targetFileId) {
      return { bg: "bg-emerald-500", text: "Configured" };
    }
    return { bg: "bg-gray-400", text: "Not Configured" };
  }, [sourceFileId, targetFileId, errors]);

  return (
    <>
      <div
        className={`relative bg-cyan-50/80 backdrop-blur-xl border-2 rounded-xl p-4 min-w-[300px] max-w-[340px] shadow-sm transition-all ${
          errors.length > 0
            ? "border-red-500 shadow-red-200"
            : isExecuting
            ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
            : executionSuccess
            ? "border-green-500 shadow-md shadow-green-200"
            : executionError
            ? "border-red-500 shadow-md shadow-red-200"
            : selected
            ? "border-cyan-500 shadow-md"
            : "border-cyan-300 hover:border-cyan-400"
        }`}
      >
        {isExecuting && (
          <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        )}
        {executionSuccess && (
          <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
                    <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                  </div>
        )}
        {executionError && (
           <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-red-500 rounded-full p-1.5 border-2 border-white">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
        </div>
        )}

        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 bg-cyan-500 border-2 border-white"
        />

        <div className="flex items-start justify-between mb-3 cursor-move">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg shadow-sm bg-cyan-100">
              <GitBranch className="w-4 h-4 text-cyan-700" />
            </div>
            <div>
              <h3 className="font-medium text-gray-800 text-sm">
                Compare Documents
              </h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div
                  className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg}`}
                />
                <p className="text-xs text-gray-600">{statusConfig.text}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NodeDocsButton nodeType="FILE-COMPARE" />
            <button
              onClick={handleDeleteNode}
              className="text-gray-500 hover:text-red-600 p-1 rounded transition"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          className="p-3 bg-white/80 border border-cyan-200 rounded-lg text-xs shadow-inner space-y-2"
          onClick={stopPropagation}
        >
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-600">Store ID:</span>
            <span className="text-cyan-800 bg-cyan-100 px-2 py-0.5 rounded text-[11px] font-medium truncate max-w-[150px]">
              {documentStoreId || "Not Set"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-600">Source:</span>
            <span className="text-cyan-800 bg-cyan-100 px-2 py-0.5 rounded text-[11px] font-medium truncate max-w-[150px]">
              {sourceFileName}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-600">Target:</span>
            <span className="text-cyan-800 bg-cyan-100 px-2 py-0.5 rounded text-[11px] font-medium truncate max-w-[150px]">
              {targetFileName}
            </span>
          </div>
        </div>

        <div className="mt-3" onClick={stopPropagation}>
          <button
            onClick={handleOpenModal}
            className="w-full py-2.5 rounded-lg bg-cyan-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-cyan-700 transition-colors"
          >
            <Settings className="w-3.5 h-3.5" /> Configure Comparison
          </button>
        </div>

        {errors.length > 0 && (
          <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 p-2 rounded">
            {errors.map((err, i) => (
              <div key={i}>• {err}</div>
            ))}
          </div>
        )}

        <Handle
          type="source"
          position={Position.Right}
          className="w-3 h-3 bg-cyan-500 border-2 border-white"
        />
      </div>

      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={handleCloseModal}
          >
            <div
              className="bg-white rounded-lg shadow-2xl w-[95vw] max-w-[1600px] h-[92vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-50 rounded">
                    <GitBranch className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Configure Document Comparison
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Drag fields from connected nodes and select source/target files.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* 3-Column Layout */}
              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                {/* Column 1: Left Sidebar (Previous Nodes) */}
                <div className="w-[340px] shrink-0 overflow-hidden">
                  <NodeDataSidebar
                    availableVariables={previousNodes}
                    onDragStart={handleModalDragStart}
                  />
                </div>

                {/* Column 2: Center - Configuration */}
                <div className="flex-1 bg-[#f9fafb] px-6 py-4 overflow-y-auto">
                  {/* Document Store ID Drop Zone */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <div className="w-5 h-5 rounded bg-cyan-100 flex items-center justify-center">
                        <GitBranch className="w-3 h-3 text-cyan-700" />
                      </div>
                      Input Mapping
                    </h3>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-700">
                        Document Store ID
                      </label>
                      <div
                        onDrop={(e) => handleDrop(e, "document_store_id")}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        className={`h-12 border-2 border-dashed rounded-lg flex items-center px-3 transition-colors ${
                          documentStoreId
                            ? "border-green-300 bg-green-50"
                            : "border-gray-300 bg-gray-50 hover:border-cyan-300"
                        }`}
                      >
                        {documentStoreId ? (
                          <div className="flex items-center justify-between w-full">
                            <span className="text-xs font-mono text-green-700 truncate pointer-events-none">
                              {documentStoreId}
                            </span>
                            <button
                              onClick={() => setDocumentStoreId("")}
                              className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 pointer-events-none">
                            Drag Document Store ID here
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Source File Selection */}
                  <div className="mb-6">
                    <h3 className="font-medium text-gray-800 mb-2 text-sm">
                      Source Document
                    </h3>
                    <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2 max-h-60 overflow-y-auto">
                      {allFiles.length > 0 ? (
                        allFiles.map((file) => (
                          <button
                            key={file._id}
                            onClick={() => setSourceFileId(file._id)}
                            className={`w-full text-left p-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
                              sourceFileId === file._id
                                ? "bg-cyan-600 text-white"
                                : "hover:bg-gray-100"
                            }`}
                          >
                            <FileText className="w-4 h-4" />
                            {file.name}
                          </button>
                        ))
                      ) : (
                        <p className="text-xs text-gray-500 text-center py-4">
                          No files available from connected nodes.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Target File Selection */}
                  <div className="mb-6">
                    <h3 className="font-medium text-gray-800 mb-2 text-sm">
                      Target Document
                    </h3>
                    <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2 max-h-60 overflow-y-auto">
                      {allFiles.length > 0 ? (
                        allFiles.map((file) => (
                          <button
                            key={file._id}
                            onClick={() => setTargetFileId(file._id)}
                            className={`w-full text-left p-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
                              targetFileId === file._id
                                ? "bg-cyan-600 text-white"
                                : "hover:bg-gray-100"
                            }`}
                          >
                            <FileText className="w-4 h-4" />
                            {file.name}
                          </button>
                        ))
                      ) : (
                        <p className="text-xs text-gray-500 text-center py-4">
                          No files available from connected nodes.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Column 3: Outputs */}
                <div className="w-[430px] bg-white border-l border-gray-200 flex flex-col flex-shrink-0">
                  <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
                    <h3 className="font-medium text-gray-800 text-sm">
                      Execution Output
                    </h3>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs">
                    {data.outputs ? (
                      <div>
                        {data.outputs.comparison_summary && (
                          <div className="mb-4">
                            <h4 className="font-semibold text-gray-700 mb-1">
                              Summary
                            </h4>
                            <p className="text-gray-600">
                              {data.outputs.comparison_summary}
                            </p>
                          </div>
                        )}

                        {data.outputs.comparision_result && (
                          <div className="mb-4">
                            <h4 className="font-semibold text-gray-700 mb-2">
                              Comparison Details
                            </h4>
                            <div className="grid grid-cols-2 gap-2 text-xs bg-gray-50 p-2 rounded-md border">
                              <div className="font-medium text-gray-500">
                                Similarity:
                              </div>
                              <div className="text-gray-800 font-semibold">
                                {data.outputs.comparision_result.Similarity}
                              </div>
                              <div className="font-medium text-gray-500">
                                Total Changes:
                              </div>
                              <div className="text-gray-800">
                                {data.outputs.comparision_result.total_changes}
                              </div>
                              <div className="font-medium text-gray-500">
                                Words Compared:
                              </div>
                              <div className="text-gray-800">
                                {data.outputs.comparision_result.words_compared}
                              </div>
                            </div>
                          </div>
                        )}

                        {data.outputs.differences?.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-700 mb-2">
                              Differences Found
                            </h4>
                            <div className="space-y-3">
                              {data.outputs.differences.map(
                                (diff: any, index: number) => (
                                  <div
                                    key={index}
                                    className="bg-gray-50 p-3 rounded-lg border border-gray-200"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <span
                                        className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                                          diff.difference_type ===
                                          "CONTEXT_CHANGE"
                                            ? "bg-yellow-100 text-yellow-800"
                                            : "bg-gray-100 text-gray-800"
                                        }`}
                                      >
                                        {diff.difference_type}
                                      </span>
                                      <span
                                        className={`text-[10px] font-medium ${
                                          diff.significance === "Medium"
                                            ? "text-orange-600"
                                            : "text-gray-500"
                                        }`}
                                      >
                                        {diff.significance} Significance
                                      </span>
                                    </div>
                                    {diff.detail && (
                                      <div className="flex items-center gap-2 my-2 p-2 bg-red-50/50 rounded-md">
                                        <span className="text-red-700 line-through">
                                          {diff.detail.From}
                                        </span>
                                        <ChevronsRight className="w-4 h-4 text-gray-400" />
                                        <span className="text-green-700">
                                          {diff.detail.To}
                                        </span>
                                      </div>
                                    )}
                                    <p className="text-gray-600 text-[11px] italic mb-1">
                                      <strong>Source:</strong>{" "}
                                      {diff.source_document_reference}
                                    </p>
                                    <p className="text-gray-600 text-[11px] italic">
                                      <strong>Target:</strong>{" "}
                                      {diff.target_document_detail}
                                    </p>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-gray-500">
                          No output to display. Run the workflow to see results.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                <div className="flex items-center gap-2 text-sm">
                  <div className="p-2 bg-white border rounded-md truncate max-w-xs">
                    <span className="text-gray-500">Source: </span>
                    <span className="font-medium text-cyan-700">
                      {sourceFileName}
                    </span>
                  </div>
                  <ChevronsRight className="w-5 h-5 text-gray-400" />
                  <div className="p-2 bg-white border rounded-md truncate max-w-xs">
                    <span className="text-gray-500">Target: </span>
                    <span className="font-medium text-cyan-700">
                      {targetFileName}
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  disabled={!sourceFileId || !targetFileId}
                  className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50"
                >
                  Save Configuration
                </button>
              </div>
            </div>{" "}
          </div>,
          document.body
        )}
    </>
  );
};

export default CompareDataNode;
